/**
 * Environment Configuration Manager
 *
 * Provides type-safe access to environment variables with validation
 * Ensures all required configuration is present before application starts
 *
 * Security: Only public keys and non-sensitive configuration should be accessed here
 * All secret keys and sensitive data must remain on the backend
 */

interface EnvironmentConfig {
  // Application Environment
  APP_ENV: 'development' | 'staging' | 'production';
  API_BASE_URL: string;
  USE_REAL_API: boolean;

  // Supabase Configuration
  SUPABASE_URL?: string;
  SUPABASE_ANON_KEY?: string;

  // Data Storage
  FORCE_LOCALSTORAGE: boolean;
  HYBRID_MODE: boolean;
  ENCRYPTION_KEY?: string;

  // External Services (Public Keys Only)
  STRIPE_PUBLIC_KEY?: string;
  GOOGLE_ANALYTICS_ID?: string;
  GOOGLE_MAPS_API_KEY?: string;
  RECAPTCHA_SITE_KEY?: string;
  VAPID_PUBLIC_KEY?: string;

  // Feature Flags
  ENABLE_AI_FEATURES: boolean;
  ENABLE_ANALYTICS: boolean;
  ENABLE_DEBUG_MODE: boolean;
  ENABLE_PWA: boolean;

  // UI/UX Configuration
  DEFAULT_LANGUAGE: 'ja' | 'en';
  DEFAULT_THEME: 'light' | 'dark' | 'auto';
  DEFAULT_DATE_FORMAT: string;
  DEFAULT_TIME_FORMAT: '12h' | '24h';

  // Performance
  ENABLE_CACHE: boolean;
  CACHE_TTL: number;
  MAX_API_RETRIES: number;
  API_TIMEOUT: number;

  // Security
  CSP_ENABLED: boolean;
  FRAME_OPTIONS: string;
}

class EnvironmentManager {
  private config: EnvironmentConfig;
  private readonly isDevelopment: boolean;
  private readonly isProduction: boolean;
  private readonly isStaging: boolean;

  constructor() {
    this.config = this.loadConfig();
    this.isDevelopment = this.config.APP_ENV === 'development';
    this.isStaging = this.config.APP_ENV === 'staging';
    this.isProduction = this.config.APP_ENV === 'production';
    this.validateConfig();
  }

  private loadConfig(): EnvironmentConfig {
    // Use import.meta.env for Vite
    const env = import.meta.env || {};

    return {
      // Application Environment
      APP_ENV: (env.VITE_APP_ENV || 'development') as EnvironmentConfig['APP_ENV'],
      API_BASE_URL: this.getSecureApiBaseUrl(env),
      USE_REAL_API: this.parseBoolean(env.VITE_USE_REAL_API, false),

      // Supabase Configuration
      SUPABASE_URL: env.VITE_SUPABASE_URL,
      SUPABASE_ANON_KEY: env.VITE_SUPABASE_ANON_KEY,

      // Data Storage
      FORCE_LOCALSTORAGE: this.parseBoolean(env.VITE_FORCE_LOCALSTORAGE, true), // Default to localStorage for MVP
      HYBRID_MODE: this.parseBoolean(env.VITE_HYBRID_MODE, false),
      ENCRYPTION_KEY: env.VITE_ENCRYPTION_KEY,

      // External Services
      STRIPE_PUBLIC_KEY: env.VITE_STRIPE_PUBLIC_KEY,
      GOOGLE_ANALYTICS_ID: env.VITE_GOOGLE_ANALYTICS_ID,
      GOOGLE_MAPS_API_KEY: env.VITE_GOOGLE_MAPS_API_KEY,
      RECAPTCHA_SITE_KEY: env.VITE_RECAPTCHA_SITE_KEY,
      VAPID_PUBLIC_KEY: env.VITE_VAPID_PUBLIC_KEY,

      // Feature Flags
      ENABLE_AI_FEATURES: this.parseBoolean(env.VITE_ENABLE_AI_FEATURES, false),
      ENABLE_ANALYTICS: this.parseBoolean(env.VITE_ENABLE_ANALYTICS, true),
      ENABLE_DEBUG_MODE: this.parseBoolean(env.VITE_ENABLE_DEBUG_MODE, false),
      ENABLE_PWA: this.parseBoolean(env.VITE_ENABLE_PWA, true),

      // UI/UX Configuration
      DEFAULT_LANGUAGE: (env.VITE_DEFAULT_LANGUAGE || 'ja') as EnvironmentConfig['DEFAULT_LANGUAGE'],
      DEFAULT_THEME: (env.VITE_DEFAULT_THEME || 'light') as EnvironmentConfig['DEFAULT_THEME'],
      DEFAULT_DATE_FORMAT: env.VITE_DEFAULT_DATE_FORMAT || 'YYYY/MM/DD',
      DEFAULT_TIME_FORMAT: (env.VITE_DEFAULT_TIME_FORMAT || '24h') as EnvironmentConfig['DEFAULT_TIME_FORMAT'],

      // Performance
      ENABLE_CACHE: this.parseBoolean(env.VITE_ENABLE_CACHE, true),
      CACHE_TTL: this.parseNumber(env.VITE_CACHE_TTL, 3600),
      MAX_API_RETRIES: this.parseNumber(env.VITE_MAX_API_RETRIES, 3),
      API_TIMEOUT: this.parseNumber(env.VITE_API_TIMEOUT, 30000),

      // Security
      CSP_ENABLED: this.parseBoolean(env.VITE_CSP_ENABLED, false),
      FRAME_OPTIONS: env.VITE_FRAME_OPTIONS || 'SAMEORIGIN',
    };
  }

  private parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
    if (value === undefined || value === '') return defaultValue;
    return value === 'true' || value === '1';
  }

  private parseNumber(value: string | undefined, defaultValue: number): number {
    if (value === undefined || value === '') return defaultValue;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  /**
   * Get API Base URL with intelligent environment-based routing
   *
   * Priority Order:
   * 1. Explicit VITE_API_BASE_URL environment variable (manual override)
   * 2. Custom domain routing based on environment:
   *    - Production (main): https://api.salonhub.co.jp
   *    - Staging (develop): https://api-staging.salonhub.co.jp
   *    - Preview (all PRs): https://preview.api.salonhub.co.jp (fixed URL)
   * 3. Local development: http://localhost:3000
   *
   * Security: Only allows localhost fallback in development mode
   */
  private getSecureApiBaseUrl(env: any): string {
    const apiBaseUrl = env.VITE_API_BASE_URL;
    const appEnv = env.VITE_APP_ENV || env.NODE_ENV || 'development';
    const vercelEnv = env.VERCEL_ENV; // Vercel environment: production | preview | development
    const vercelBranch = env.VERCEL_GIT_COMMIT_REF; // Branch name (e.g., "main", "develop", "feature/xyz")

    // PRIORITY 1: Explicit environment variable override
    if (apiBaseUrl) {
      console.log(`✅ Using explicit VITE_API_BASE_URL: ${apiBaseUrl}`);
      return apiBaseUrl;
    }

    // PRIORITY 2: Production environment (main branch)
    if (vercelEnv === 'production' || vercelBranch === 'main') {
      const productionUrl = 'https://api.salonhub.co.jp';
      console.log(`✅ Production environment detected, using: ${productionUrl}`);
      return productionUrl;
    }

    // PRIORITY 3: Staging environment (develop branch)
    if (vercelBranch === 'develop' || appEnv === 'staging') {
      const stagingUrl = 'https://api-staging.salonhub.co.jp';
      console.log(`✅ Staging environment detected, using: ${stagingUrl}`);
      return stagingUrl;
    }

    // PRIORITY 4: Preview/PR environment (all PRs use fixed preview URL)
    // Fixed URL eliminates Frontend/Backend URL synchronization issues
    if (vercelEnv === 'preview') {
      const previewUrl = 'https://preview.api.salonhub.co.jp';
      console.log(`✅ Preview environment detected`);
      console.log(`   Frontend → Backend (fixed): ${previewUrl}`);
      return previewUrl;
    }

    // PRIORITY 5: Local development and test
    if (appEnv === 'development' || appEnv === 'test') {
      console.warn(`⚠️ Using localhost fallback for API_BASE_URL in ${appEnv} environment`);
      return 'http://localhost:3000';
    }

    // PRIORITY 6: Fail-safe fallback - should never reach here
    const errorMessage =
      `❌ Unable to determine API_BASE_URL:\n` +
      `   VERCEL_ENV=${vercelEnv}\n` +
      `   VERCEL_GIT_COMMIT_REF=${vercelBranch}\n` +
      `   APP_ENV=${appEnv}\n` +
      `   Please set VITE_API_BASE_URL in Vercel environment variables or check your deployment configuration.`;

    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  private validateConfig(): void {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields for production
    if (this.isProduction) {
      // Critical security validation (hard errors)
      if (!import.meta.env?.VITE_API_BASE_URL) {
        errors.push('VITE_API_BASE_URL is required in production - localhost fallback not allowed');
      }

      // Security warnings (recommended but not blocking)
      if (!this.config.ENCRYPTION_KEY) {
        warnings.push('ENCRYPTION_KEY not configured - data encryption disabled (recommended for production)');
      }

      // Optional services (warnings only)
      if (!this.config.STRIPE_PUBLIC_KEY) {
        warnings.push('STRIPE_PUBLIC_KEY not configured - payment features will be disabled');
      }
      if (!this.config.GOOGLE_ANALYTICS_ID) {
        warnings.push('GOOGLE_ANALYTICS_ID not configured - analytics tracking will be disabled');
      }
    }

    // Staging environment validation
    if (this.isStaging) {
      if (!import.meta.env?.VITE_API_BASE_URL) {
        errors.push('VITE_API_BASE_URL is required in staging - localhost fallback not allowed');
      }
    }

    // Supabase validation (if not forcing localStorage)
    if (!this.config.FORCE_LOCALSTORAGE && !this.config.HYBRID_MODE) {
      // Only warn if explicitly trying to use Supabase mode
      if (!this.config.SUPABASE_URL) {
        warnings.push('SUPABASE_URL not configured - localStorage mode will be used');
      }
      if (!this.config.SUPABASE_ANON_KEY) {
        warnings.push('SUPABASE_ANON_KEY not configured - localStorage mode will be used');
      }
    }

    // Display warnings (non-blocking)
    if (warnings.length > 0) {
      console.warn('⚠️ Environment Configuration Warnings:\n' + warnings.join('\n'));
    }

    // Log warnings in development, throw errors in production
    if (errors.length > 0) {
      const message = `Environment configuration errors:\n${errors.join('\n')}`;

      if (this.isProduction) {
        throw new Error(message);
      } else {
        console.warn('⚠️ Environment Configuration Warning:', message);
      }
    }

    // Security warnings
    if (this.config.ENABLE_DEBUG_MODE && this.isProduction) {
      console.warn('⚠️ Security Warning: Debug mode is enabled in production!');
    }

    // Log configuration summary (excluding sensitive data)
    if (this.config.ENABLE_DEBUG_MODE) {
      console.log('🔧 Environment Configuration:', {
        environment: this.config.APP_ENV,
        apiBaseUrl: this.config.API_BASE_URL,
        useRealApi: this.config.USE_REAL_API,
        forceLocalStorage: this.config.FORCE_LOCALSTORAGE,
        features: {
          ai: this.config.ENABLE_AI_FEATURES,
          analytics: this.config.ENABLE_ANALYTICS,
          pwa: this.config.ENABLE_PWA,
        },
      });
    }
  }

  // Getter methods for accessing configuration
  get<K extends keyof EnvironmentConfig>(key: K): EnvironmentConfig[K] {
    return this.config[key];
  }

  // Convenience methods
  isDev(): boolean {
    return this.isDevelopment;
  }

  isProd(): boolean {
    return this.isProduction;
  }

  isStage(): boolean {
    return this.isStaging;
  }

  isDebugMode(): boolean {
    return this.config.ENABLE_DEBUG_MODE;
  }

  // Feature flag checks
  isFeatureEnabled(feature: 'ai' | 'analytics' | 'pwa' | 'cache'): boolean {
    switch (feature) {
      case 'ai':
        return this.config.ENABLE_AI_FEATURES;
      case 'analytics':
        return this.config.ENABLE_ANALYTICS;
      case 'pwa':
        return this.config.ENABLE_PWA;
      case 'cache':
        return this.config.ENABLE_CACHE;
      default:
        return false;
    }
  }

  // API configuration
  getApiConfig() {
    return {
      baseUrl: this.config.API_BASE_URL,
      timeout: this.config.API_TIMEOUT,
      retries: this.config.MAX_API_RETRIES,
      useRealApi: this.config.USE_REAL_API,
    };
  }

  // Stripe configuration (public only)
  getStripeConfig() {
    return {
      publicKey: this.config.STRIPE_PUBLIC_KEY,
      // Note: Secret keys are never exposed to frontend
    };
  }

  // Supabase configuration
  getSupabaseConfig() {
    return {
      url: this.config.SUPABASE_URL,
      anonKey: this.config.SUPABASE_ANON_KEY,
      // Note: Service role key is never exposed to frontend
    };
  }

  // Storage configuration
  getStorageConfig() {
    return {
      forceLocalStorage: this.config.FORCE_LOCALSTORAGE,
      hybridMode: this.config.HYBRID_MODE,
      encryptionKey: this.config.ENCRYPTION_KEY,
    };
  }

  // UI configuration
  getUIConfig() {
    return {
      language: this.config.DEFAULT_LANGUAGE,
      theme: this.config.DEFAULT_THEME,
      dateFormat: this.config.DEFAULT_DATE_FORMAT,
      timeFormat: this.config.DEFAULT_TIME_FORMAT,
    };
  }
}

// Create singleton instance
const env = new EnvironmentManager();

// Export both the instance and the class for testing
export { EnvironmentManager };
export default env;